'use client';

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import type { NextPage } from 'next';
import Image from 'next/image';
import Link from 'next/link';
import logoImage from 'public/images/logo.svg';
import { useState } from 'react';

import type { BlogPost } from '@/src/lib/ghost';

dayjs.extend(relativeTime);

interface CardProps {
  post: BlogPost;
}

export const Card: NextPage<CardProps> = ({ post }: CardProps) => {
  const [isHovered, setIsHovered] = useState(false);

  const imageExtenstion = post.feature_image?.split('.').pop();
  const imageUrl = `/images/blog/icons/${post.slug}.${imageExtenstion}`;
  return (
    <Link href={{ pathname: `/blog/${post.slug}` }}>
      <div
        className="source-sans-3 cursor-pointer rounded-xl shadow-xl transition duration-300 ease-in-out hover:-translate-y-2 hover:shadow-2xl"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="relative h-56 rounded-t-xl">
          <div className="flex">
            <Image
              src={imageUrl}
              alt={post.title!}
              layout="fill"
              objectFit="cover"
              className="rounded-t-xl bg-cover bg-center"
            />
            <span
              className={`capitalizemd:mx-5 lg:-mt-50 absolute ml-6 mt-6 flex rounded-3xl border-2 border-white text-sm text-grey-800 md:mb-4 md:mt-5 lg:mx-6 lg:mb-2 ${
                isHovered
                  ? 'dark-shadow transition duration-300 ease-in-out'
                  : ''
              }`}
            >
              <Image
                src={logoImage}
                alt="profile"
                className="h-8 w-8 rounded-full"
                width="32"
                height="32"
              />
            </span>
          </div>
        </div>
        <div className="p-6">
          <span className="pb-2 text-[14px] text-grey-500 hover:text-blue-700">
            <Link href={{ pathname: `/blog/tag/${post.primary_tag?.slug}` }}>
              {post.primary_tag?.name}
            </Link>
          </span>
          <div
            className="my-2 text-xl text-grey-700"
            style={{
              height: '64px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: '2',
              WebkitBoxOrient: 'vertical',
            }}
          >
            {post.title}
          </div>
          <div className="pt-2 text-sm text-grey-500">
            <span className="flex flex-row text-center">
              {dayjs(post.published_at).fromNow() || 'a few seconds ago'}
              <span className="px-2">&bull;</span>
              <span>{post.reading_time} min read</span>
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
};
